// QA 01-10-2026 (B10) · El resumen de las 29 respuestas del instructivo, legible.
//
// En el FAR-03 (sección «Información adicional del EA grave») y en la ampliación del informe
// las 29 respuestas salían como una sola columna de líneas —«14. Signos…: si — fiebre…»— que
// nadie leía. Aquí cada punto es una fila con su PREGUNTA, su RESPUESTA y su COMENTARIO, y los
// puntos van agrupados por los bloques temáticos de la pestaña «Investigación», cada bloque
// con su borde y su título, como las secciones del registro del caso.
//
// Los textos de las preguntas son los del instructivo (`corto`), sin tocar. La numeración es
// la oficial: el bloque solo agrupa (`BLOQUES_INSTRUCTIVO`). Una fila sin `valor` por partes
// —versión congelada antes de este arreglo— se lee por su `texto`, tal cual lo dio el servidor.

import { useId } from 'react';
import { Lock } from 'lucide-react';
import { Mono, StatusBadge } from './ui';
import { BLOQUES_INSTRUCTIVO, INSTRUCTIVO } from '../data/instructivo';
import { etiquetaDeLaRespuesta } from '../lib/etiquetas';

/** Un punto respondido, como lo traen el informe (`filas` del numeral 9.10) o la ampliación. */
export type RespuestaResumida = {
  numero: number;
  corto: string;
  /** Código de la respuesta (`si` / `no` / `no_aplica`); `null` sin responder; `undefined` si la fila no lo trae. */
  valor?: string | null;
  observacion?: string | null;
  derivada?: boolean;
  /** La observación está enmascarada por ser dato de salud (RN-09). */
  observacion_protegida?: boolean;
  /** La línea entera del servidor: se usa solo si la fila no trae la respuesta por partes. */
  texto?: string;
};

const DESCRIPTIVOS = new Set(INSTRUCTIVO.filter((p) => p.descriptivo).map((p) => p.n));

/** La línea del servidor sin su número delante («14. Signos…»): el número ya va en su columna. */
const sinNumero = (texto: string, numero: number): string => {
  const prefijo = `${numero}. `;
  return texto.startsWith(prefijo) ? texto.slice(prefijo.length) : texto;
};

// Los `{' '}` entre columnas no ocupan sitio en la rejilla; separan las palabras al leer o
// copiar la fila («1. Manejo… Sí»), que pegadas se leían «1.Manejo…Sí».
function FilaDeRespuesta({ r, testid }: Readonly<{ r: RespuestaResumida; testid: string }>) {
  const porPartes = r.valor !== undefined;
  return (
    <li data-testid={`${testid}-fila`} className="px-3 py-2 grid grid-cols-[2rem_minmax(0,1fr)] sm:grid-cols-[2rem_minmax(0,1fr)_auto] gap-x-2 gap-y-1 items-baseline text-meta">
      <Mono className="text-meta">{r.numero}.</Mono>{' '}
      {porPartes ? (
        <>
          <span className="font-medium text-ink min-w-0 break-words">{r.corto}</span>{' '}
          <span className="col-start-2 sm:col-start-3 sm:row-start-1">
            {r.valor
              ? (
                <StatusBadge data-testid={`${testid}-respuesta`} tone={r.derivada ? 'info' : 'neutral'}>
                  {etiquetaDeLaRespuesta(r.valor, DESCRIPTIVOS.has(r.numero))}{r.derivada ? ' · derivada' : ''}
                </StatusBadge>
              )
              : <span data-testid={`${testid}-respuesta`} className="text-meta text-ink-soft">Sin respuesta</span>}
          </span>{' '}
          {r.observacion && (
            <p className="col-start-2 sm:col-span-2 text-ink-soft break-words">
              <span className="font-medium text-ink">Comentario: </span>
              {r.observacion_protegida
                ? <span className="inline-flex items-center gap-1"><Lock size={12} aria-hidden="true" /> {r.observacion}</span>
                : r.observacion}
            </p>
          )}
        </>
      ) : (
        <span className="min-w-0 break-words sm:col-span-2">{sinNumero(r.texto ?? r.corto, r.numero)}</span>
      )}
    </li>
  );
}

export default function RespuestasDelInstructivo({ respuestas, contexto, nivelDelTitulo = 3, 'aria-label': ariaLabel }: Readonly<{
  respuestas: RespuestaResumida[];
  /** Prefijo de los `data-testid` (`informe`, `ampliacion`): `<contexto>-instructivo[-bloque|-fila|-respuesta]`. */
  contexto: string;
  /** El nivel de los títulos de bloque, uno por debajo del que los contiene. */
  nivelDelTitulo?: 3 | 4;
  'aria-label': string;
}>) {
  const id = useId();
  const testid = `${contexto}-instructivo`;
  const Titulo = nivelDelTitulo === 4 ? 'h4' : 'h3';
  const bloques = BLOQUES_INSTRUCTIVO.map((b) => ({
    ...b, filas: respuestas.filter((r) => r.numero >= b.desde && r.numero <= b.hasta),
  })).filter((b) => b.filas.length > 0);
  // Lo que no cae en ningún bloque (un punto que el instructivo no tuviera) no se pierde.
  const sueltas = respuestas.filter((r) => !BLOQUES_INSTRUCTIVO.some((b) => r.numero >= b.desde && r.numero <= b.hasta));
  return (
    <div data-testid={testid} aria-label={ariaLabel} role="group" className="space-y-3">
      {bloques.map((b) => (
        <section key={b.id} data-testid={`${testid}-bloque`} aria-labelledby={`${id}-${b.id}`} className="border border-hairline rounded-xl overflow-hidden">
          <Titulo id={`${id}-${b.id}`} className="bg-canvas border-b border-hairline px-3 py-2 text-compacto font-semibold text-ink">
            {b.titulo} <span className="font-normal text-ink-soft">· puntos {b.desde} a {b.hasta}</span>
          </Titulo>
          <ol className="divide-y divide-hairline" aria-labelledby={`${id}-${b.id}`}>
            {b.filas.map((r) => <FilaDeRespuesta key={r.numero} r={r} testid={testid} />)}
          </ol>
        </section>
      ))}
      {sueltas.length > 0 && (
        <ol className="border border-hairline rounded-xl divide-y divide-hairline">
          {sueltas.map((r) => <FilaDeRespuesta key={r.numero} r={r} testid={testid} />)}
        </ol>
      )}
    </div>
  );
}
